/**
 * The screener's rows, reduced to what fits a phone.
 *
 * A row on the desktop is twenty columns; a row here is a symbol, a name and
 * **two numbers**. Which two is not a new setting — it is the section's own
 * `ScreenerConfig.columns`, in order, minus the ones already spoken for (the
 * symbol itself) and the ones the user hid. A mobile-only column list would be a
 * second answer to a question the workspace already answers, and the two would
 * drift the first time somebody reordered their columns.
 *
 * Pure, and therefore tested: the filtering and the picking are the parts that
 * can be wrong in a way nobody sees, and `MobileRows` around it is markup.
 */

import type { ScreenerColumn, ScreenerConfig } from "@qufolio/model";
import type { ScreenerColumnMeta, ScreenerFrame, ScreenerRow } from "@qufolio/transport";
import {
  evaluate,
  evaluateFilterNode,
  formatNotation,
  isRowScoped,
  matchesFilter,
  resolveImageUrl,
  testPredicate,
} from "@qufolio/screener-grid";

/** Field names that are the row's identity rather than one of its numbers. */
const IDENTITY = new Set(["symbol", "ticker", "name", "company", "companyname"]);

/**
 * How many numbers a row shows: **four, as two per line.**
 *
 * The row already has two lines — symbol over name — and the right-hand side
 * was using only the first of them. Filling both doubles what a row carries for
 * no extra height, which is the whole reason to stack them rather than widen.
 *
 * Four and not six: at 390px the right block is ~230px, so two columns of ~110
 * each hold a formatted number and a short label. A third column per line drops
 * each to ~75 and starts truncating the values it exists to show.
 */
export const MOBILE_COLUMNS = 6;

/** Columns per line, so the renderer and the picker agree on the shape. */
export const MOBILE_COLUMNS_PER_LINE = 3;

/**
 * On an iPad: six a line, twelve a page — the wider row the approved design
 * gives the tablet (`docs/features/mobile.md`, the screener on a touch screen).
 */
export const TABLET_COLUMNS_PER_LINE = 6;

/** Letters of a column's header kept as its tag. Five fits a 66px track. */
export const LABEL_LETTERS = 5;

/**
 * The two numbers a phone row wants, in order, when the frame has them.
 *
 * Taking the workspace's first two columns instead was the obvious rule and
 * produced rows reading `—  Technology`: a blank and a sector name, because
 * that is what happened to sit at the front of a column list built for a
 * twenty-wide desktop grid. On a row with space for two numbers, the last trade
 * and the move are what the row is *for* — every other column is a reason to
 * open the chart, not a thing to scan a list by.
 *
 * Still only a preference: a frame without them falls back to the config, so a
 * screen of something other than prices is not left blank.
 */
const PREFERRED_FIELDS = ["close", "changePercent"] as const;

/**
 * The touch screener's search: contains, case-insensitive, on the ticker or the
 * company name — the grid's own `matchesSymbolSearch` rule, over the phone's rows.
 */
export function searchRows<T extends { symbol: string; name?: string }>(rows: readonly T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...rows];
  return rows.filter((r) => r.symbol.toLowerCase().includes(q) || (r.name ?? "").toLowerCase().includes(q));
}

export interface MobileRow {
  symbol: string;
  name?: string;
  /**
   * The row's logo, when the workspace declares an image column.
   *
   * Resolved through `resolveImageUrl` — the grid's own function over the
   * contract's own `ScreenerColumn.imageUrl` template — so the phone and the
   * desktop fetch the same URL and no second scheme is invented here.
   *
   * Undefined when no image column is configured, or when the template's
   * fields are missing for this row: a URL with a hole in it is a 404 per row.
   */
  logoUrl?: string;
  /** The chosen columns, already formatted, in order. */
  cells: MobileCell[];
}

export interface MobileCell {
  field: string;
  header: string;
  text: string;
  /**
   * The theme **token** this cell's text should take, or undefined for the
   * ordinary colour. A token and never a colour, for D13's reason — a hex here
   * would survive a theme switch and stop matching everything around it.
   */
  token?: string;
  /**
   * A short tag drawn before the number, or undefined for the obvious ones.
   *
   * Four numbers with no headers are four unlabelled numbers. Price and change
   * identify themselves by shape — `305.92`, `+1.2%` — so labelling those two
   * would spend the width that makes the other two readable at all.
   */
  label?: string;
}

/** The detail frame, which is the one whose rows are symbols. */
export function detailFrame(frames: readonly ScreenerFrame[]): ScreenerFrame | undefined {
  return frames.find((f) => f.name === "detail") ?? frames[0];
}

/**
 * Which columns a phone row shows.
 *
 * Hidden ones are skipped for the obvious reason, and identity ones because the
 * symbol is already the row's title — printing it again as a "column" would
 * spend one of two slots restating the heading.
 */
/**
 * Every column the phone can show, in order, as pages of {@link MOBILE_COLUMNS}.
 *
 * The row shows one page and swipes between them with the symbol pinned, so a
 * twenty-column screen is reachable without a horizontal scrollbar and without
 * the identity ever leaving the screen.
 *
 * **One page index for the whole list, not one per row** — that is the caller's
 * job, and it is the decision that matters: rows each remembering their own
 * page would put six different columns down one screen, and a column of figures
 * that are not the same figure is worse than not showing them.
 */
export function columnPages(
  config: ScreenerConfig,
  fields: readonly string[] = [],
  perPage = MOBILE_COLUMNS,
): ScreenerColumn[][] {
  const all = mobileColumns(config, fields, Number.MAX_SAFE_INTEGER);
  if (all.length === 0) return [];
  const pages: ScreenerColumn[][] = [];
  for (let i = 0; i < all.length; i += perPage) pages.push(all.slice(i, i + perPage));
  return pages;
}

export function mobileColumns(
  config: ScreenerConfig,
  fields: readonly string[] = [],
  limit = MOBILE_COLUMNS,
): ScreenerColumn[] {
  const authored = config.columns ?? [];
  const usable = authored.filter(
    (c) =>
      !c.hide &&
      !IDENTITY.has(c.field.toLowerCase()) &&
      // An image column is the row's logo, not one of its numbers. Left in, it
      // rendered the template's *source field* as text — a ticker or an id in a
      // column meant to hold a picture.
      c.type !== "image",
  );

  // Price and move lead, when the frame carries them — see PREFERRED_FIELDS.
  // The user's own column definition is reused when there is one, so a renamed
  // header or a chosen precision still applies.
  const leading = PREFERRED_FIELDS.filter((f) => fields.includes(f)).map(
    (field) => authored.find((c) => c.field === field) ?? { field },
  );

  // Then the workspace's own columns, in its order, to fill the remaining
  // slots. Skipping the two already taken keeps `close` from appearing twice
  // when it is also listed in the grid.
  const taken = new Set(leading.map((c) => c.field));

  /**
   * **With no authored columns, the frame's own fields are the columns.**
   *
   * `ScreenerConfig.columns` is an override list, not a manifest — a workspace
   * that has never had its columns edited has none at all, and the grid then
   * shows whatever the frame carries. Reading only the config gave such a
   * workspace exactly the two preferred fields: one page, no swipe, and a
   * phone showing two numbers where the desktop showed twenty.
   */
  const source: ScreenerColumn[] =
    usable.length > 0
      ? usable
      : fields
          .filter((f) => !IDENTITY.has(f.toLowerCase()))
          .map((field) => ({ field }));

  const rest = source.filter((c) => !taken.has(c.field));

  return [...leading, ...rest].slice(0, limit);
}

/**
 * The rows to draw: filtered, sorted, and formatted.
 *
 * **The filter is the screener's own evaluator**, not a reimplementation —
 * `matchesFilter` is what `doesExternalFilterPass` calls in the grid, so a saved
 * filter cannot mean one thing on a desktop and another on a phone. That
 * mattered enough to import a package for: the DSL arm alone has an evaluator
 * behind it that nothing here should own a second copy of.
 */
export function mobileRows(
  frames: readonly ScreenerFrame[],
  config: ScreenerConfig,
  meta: readonly ScreenerColumnMeta[],
  page = 0,
  perPage = MOBILE_COLUMNS,
): MobileRow[] {
  const frame = detailFrame(frames);
  if (!frame) return [];

  const fields = frame.fields.map((f) => f.name);
  const pages = columnPages(config, fields, perPage);
  // Clamped rather than empty: a page index outliving a column being removed
  // would otherwise blank every row's numbers with nothing on screen saying why.
  const columns = pages.length === 0 ? [] : (pages[Math.min(page, pages.length - 1)] ?? []);
  const symbolField = symbolFieldOf(frame);
  const nameField = nameFieldOf(frame);

  const rows = frame.rows.filter((row) => matchesFilter(config.filter, row));
  const sorted = sortRows(rows, config);

  // The first image column the workspace declares. More than one is a desktop
  // arrangement a 390px row has no space for, so the first wins rather than
  // the last silently replacing it.
  const logo = (config.columns ?? []).find((c) => c.type === "image" && c.imageUrl);

  return sorted.map((row) => ({
    symbol: String(row[symbolField] ?? "").toUpperCase(),
    name: nameField ? textOf(row[nameField]) : undefined,
    logoUrl: logo?.imageUrl ? resolveImageUrl(logo.imageUrl, row) : undefined,
    cells: columns.map((column, i) => ({
      field: column.field,
      header: column.header ?? column.field,
      text: cellText(row[column.field], column, meta),
      token: cellToken(row, column),
      label: cellLabel(column, i),
    })),
  }));
}

/**
 * Sorted by the section's own `sort`, and otherwise left as the server sent it.
 *
 * Server order is meaningful — a screen is usually already ranked — so an
 * arbitrary client-side default would throw away the one ordering somebody
 * actually chose.
 */
function sortRows(rows: readonly ScreenerRow[], config: ScreenerConfig): ScreenerRow[] {
  const sort = config.sort?.[0];
  if (!sort) return [...rows];
  const sign = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const left = a[sort.field];
    const right = b[sort.field];
    // **Outside the sign, deliberately.** A screen with no value for a column is
    // missing data, and it belongs at the bottom whichever way the column is
    // sorted — multiplying this by the direction puts every blank row at the top
    // of a descending sort, burying the rows that actually have the number.
    const blanks = blankOrder(left, right);
    if (blanks !== 0) return blanks;
    return compare(left, right) * sign;
  });
}

/** −1/1 when exactly one side is blank, 0 when the question does not arise. */
function blankOrder(a: unknown, b: unknown): number {
  const aBlank = a == null || a === "";
  const bBlank = b == null || b === "";
  if (aBlank === bBlank) return 0;
  return aBlank ? 1 : -1;
}

function compare(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** The column's own notation and precision, falling back to the server's. */
function cellText(
  value: unknown,
  column: ScreenerColumn,
  meta: readonly ScreenerColumnMeta[],
): string {
  if (value == null || value === "") return "—";
  const notation = column.notation ?? notationFromMeta(column.field, meta);
  const formatted = formatNotation(value, notation, column.precision);
  if (formatted !== null) return formatted;
  if (typeof value === "number") {
    // A percentage reads as a number without its sign and its unit: `1.24`
    // beside a price is indistinguishable from a second price. The grid can
    // rely on a column header to say which is which; a phone row has none.
    if (isPercent(column.field)) {
      const sign = value > 0 ? "+" : "";
      return `${sign}${value.toFixed(column.precision ?? 2)}%`;
    }
    return value.toFixed(column.precision ?? defaultPrecision(value));
  }
  return String(value);
}

/**
 * Two decimals for a price, none for a count.
 *
 * A guess, but a bounded one: this only runs where neither the column nor the
 * server said anything, and "1,234.00 shares" reads worse than being wrong
 * about a fractional index level.
 */
function defaultPrecision(value: number): number {
  return Number.isInteger(value) ? 0 : 2;
}

function notationFromMeta(
  field: string,
  meta: readonly ScreenerColumnMeta[],
): ScreenerColumn["notation"] {
  const found = meta.find((m) => m.field === field);
  return (found as { notation?: ScreenerColumn["notation"] } | undefined)?.notation;
}

/**
 * What colour this cell's text should be, as a theme token.
 *
 * **The same three tiers the grid uses** (`cellStyleFor` in
 * `screener-grid/lib/columns.ts`), evaluated with the same functions: `colorBy`
 * is an expression returning a token and short-circuits; otherwise `format`
 * rules are tried in order and the last match wins, each `when` being either a
 * predicate against this cell or a filter node against the whole row.
 *
 * Colouring by the sign of the number instead was the obvious shortcut and is
 * wrong twice over: a price is not green for being positive, and a screen whose
 * author coloured `symbol` by `changePercent` would lose it entirely. Rebuilding
 * the tiers here would also be a second implementation of contract semantics —
 * the thing this file exists to avoid.
 *
 * Only the foreground is taken. The grid's rules can set a background too, but a
 * 56px row with a filled cell in it reads as a selection on a phone, and there
 * is no column header above it to say otherwise.
 */
function cellToken(row: ScreenerRow, column: ScreenerColumn): string | undefined {
  if (column.colorBy) {
    // Null is "no opinion for this row" rather than an error — the usual shape
    // of an iff() whose branches only cover some cases.
    const token = evaluate(column.colorBy, row);
    return token == null ? undefined : String(token);
  }
  let token: string | undefined;
  for (const rule of column.format ?? []) {
    const matched = isRowScoped(rule.when)
      ? evaluateFilterNode(rule.when, row)
      : testPredicate(rule.when, row[column.field]);
    // `fg` only. A rule's `bg` is dropped here on purpose: a filled cell in a
    // 56px row reads as a selection on a phone, and there is no column header
    // above it to say what the fill means.
    if (matched && rule.style?.fg) token = rule.style.fg;
  }
  if (token) return token;

  /**
   * A percentage carries its sign as its meaning, so it is coloured by it.
   *
   * Only where the workspace said nothing — a rule above always wins. And only
   * for a *percentage*: a price is not green for being above zero, which is why
   * this asks the field's name rather than the value's sign alone. The desktop
   * grid gets the same effect from format rules somebody authored once; a phone
   * row shows two numbers with no headers and cannot wait for that.
   */
  const value = row[column.field];
  if (isPercent(column.field) && typeof value === "number" && value !== 0) {
    return value > 0 ? "positive" : "negative";
  }
  return undefined;
}

/**
 * The tag drawn before a value, or undefined when the number speaks for itself.
 *
 * The leading pair is price and change — see `PREFERRED_FIELDS` — and both are
 * recognisable without help. Everything after them is whatever the workspace
 * lists, so it needs naming, and the header is shortened to fit: a column of
 * ~110px holds a formatted number and about four characters of label.
 */
function cellLabel(column: ScreenerColumn, index: number): string | undefined {
  if (index < PREFERRED_FIELDS.length) return undefined;
  const name = column.header ?? column.field;
  // Cut, never ellipsised: a `…` in a 9px tag costs a third of it to say
  // something the reader can already see from the missing letters.
  return name.slice(0, LABEL_LETTERS);
}

/**
 * Whether a field is a percentage, by name.
 *
 * By name because nothing else says so: the frame is numbers, and `1.24` is a
 * price or a move depending only on what it is called. `screener_columns`
 * normalises upstream names to camelCase server-side, so the suffix is stable
 * enough to test — `changePercent`, `wChangePercent`, and anything a timeframe
 * prefix produces from them.
 */
function isPercent(field: string): boolean {
  const name = field.toLowerCase();
  return name.endsWith("percent") || name.endsWith("pct") || name.endsWith("chg");
}

function textOf(value: unknown): string | undefined {
  const text = value == null ? "" : String(value);
  return text === "" ? undefined : text;
}

/** Whichever field the frame calls its symbol. */
function symbolFieldOf(frame: ScreenerFrame): string {
  const named = frame.fields.find((f) => IDENTITY.has(f.name.toLowerCase()));
  return named?.name ?? frame.fields[0]?.name ?? "symbol";
}

function nameFieldOf(frame: ScreenerFrame): string | undefined {
  return frame.fields.find((f) => {
    const name = f.name.toLowerCase();
    return name === "name" || name === "company" || name === "companyname";
  })?.name;
}
